import {
  DATE_FORMAT,
  LS_REMEMBERED_USER_EMAIL,
  LS_THEME,
  LS_VIEWED_PATENTS,
  MAX_UPLOAD_FILE_SIZE,
} from '@lib/constants'
import { Element as HtmlParserElement } from 'html-react-parser'
import { P, match } from 'ts-pattern'
import { find, identity, isNumber, isUndefined } from 'lodash'
import { themeSchema } from './types'
import { z } from 'zod'
import dayjs from 'dayjs'
import toast from 'react-hot-toast'
import type { HTMLReactParserOptions } from 'html-react-parser'
import type { Metadata } from 'next'
import type { PriorArt } from '@packages/utilities/schemas'
import type { ThemeOptions } from './types'
export {
  countHighlights,
  highlightKeywords,
  convertHexColorToRgbWithOpacity,
} from '@packages/utilities/utils'
export type { ColoredHighlightCount } from '@packages/utilities/utils'

export const DEFAULT_TOAST_DURATION = 5000

export const getMetadata = (pageTitle: string): Metadata => {
  return {
    title: pageTitle,
    openGraph: {
      title: pageTitle,
    },
  }
}

type ToastMessage = Parameters<typeof toast.success>[0]

export const showSuccessToast = (message: ToastMessage) => {
  if (message) {
    toast.success(message, { duration: DEFAULT_TOAST_DURATION })
  }
}

export const showErrorToast = (data: { error: ToastMessage } | string) => {
  // We disable `any` rules because type of ToastMessage is `any`, so we don't have a better type.

  const error = match(data)
    .returnType<ToastMessage>()
    .with(P.string, identity)

    .with({ error: P.not(P.nullish) }, ({ error }) => error)
    .otherwise(() => 'Something went wrong')
  toast.error(error, { duration: DEFAULT_TOAST_DURATION })
}

export const getLSTheme = (): ThemeOptions | '' | null => {
  const localStorageEntry = localStorage.getItem(LS_THEME)
  const parsedLocalStorageValue =
    localStorageEntry && themeSchema.parse(JSON.parse(localStorageEntry))
  return parsedLocalStorageValue
}

export const setLSTheme = (theme: ThemeOptions): void => {
  localStorage.setItem('theme', JSON.stringify(theme))
}

export const getRememberedUserEmail = (): string | null => {
  return localStorage.getItem(LS_REMEMBERED_USER_EMAIL)
}

export const setRememberedUserEmail = (email: string): void => {
  localStorage.setItem(LS_REMEMBERED_USER_EMAIL, email)
}

export const createViewedPatentsSchema = (project: string) =>
  z.object({
    [project]: z
      .array(z.object({ pn: z.string(), when: z.number() }))
      .default([]),
  })

export type ViewedPatent = z.infer<
  ReturnType<typeof createViewedPatentsSchema>
>[string][number]

export const getViewedPatents = (project: string): ViewedPatent[] => {
  try {
    const localStorageEntry = localStorage.getItem(LS_VIEWED_PATENTS)
    const parsedLocalStorageValue: unknown =
      localStorageEntry && JSON.parse(localStorageEntry)
    const viewedPatents = createViewedPatentsSchema(project).parse(
      parsedLocalStorageValue
    )[project]
    return viewedPatents ?? []
  } catch {
    // NOTE: reset the local storage value in case of any error
    localStorage.setItem(LS_VIEWED_PATENTS, JSON.stringify({}))
    return []
  }
}

export const setViewedPatent = (project: string, patent: string): void => {
  const localStorageEntry = localStorage.getItem(LS_VIEWED_PATENTS)
  const parsedLocalStorageValue = localStorageEntry
    ? createViewedPatentsSchema(project).parse(JSON.parse(localStorageEntry))
    : {}
  const patents = getViewedPatents(project)
  const patentFound = find(patents, ['pn', patent])
  const viewedPatent = { pn: patent, when: Date.now() }
  if (!patentFound) {
    patents.push(viewedPatent)
  }
  parsedLocalStorageValue[project] = patents
  localStorage.setItem(
    LS_VIEWED_PATENTS,
    JSON.stringify(parsedLocalStorageValue)
  )
}

export function parseDateTime(dateTime: string | Date): string {
  return dayjs(dateTime).format(DATE_FORMAT + ' hh:mmA')
}

export function parsePrice(cents = 0, currency = 'USD'): string {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency,
  })
}

export const checkFileSize = (
  files: FileList | File[]
): { sizeValid: boolean; sizeErr: string } => {
  let sizeValid = true
  let sizeError = ''
  ;[...files].every((file) => {
    if (file.size > MAX_UPLOAD_FILE_SIZE) {
      sizeValid = false
      sizeError = `File size cannot exceed ${
        MAX_UPLOAD_FILE_SIZE / 1024 / 1024
      }MB`
      return false
    }
    return true
  })

  return { sizeValid, sizeErr: sizeError }
}

export const jwtDecode = (token: string) => {
  const jwtSchema = z
    .object({
      exp: z.number(),
    })
    .default({ exp: 0 })
  return jwtSchema.parse(
    JSON.parse(Buffer.from(token.split('.')[1]!, 'base64').toString())
  )
}

export const getSelectionHtml = () => {
  let html = ''
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (window.getSelection !== undefined) {
    const sel = window.getSelection()
    if (sel?.rangeCount) {
      const container = document.createElement('p')
      for (
        let index = 0, rangeLength = sel.rangeCount;
        index < rangeLength;
        index++
      ) {
        container.append(sel.getRangeAt(index).cloneContents())
      }
      html = container.innerHTML
    }
  }
  return html
}

export const isTextNode = (node: Node): node is Text => {
  return node.nodeType === Node.TEXT_NODE
}

export const markAsSaveable: HTMLReactParserOptions['replace'] = (domNode) => {
  if (domNode instanceof HtmlParserElement) {
    domNode.attribs['data-is-saveable-ancestor-container'] = 'true'
  }

  return domNode
}

export const rootDomain = (hostname: string): string => {
  let parts = hostname.split('.')
  if (parts.length <= 2) {
    return hostname
  }

  parts = parts.slice(-3)
  if (['co', 'com'].includes(parts[1]!)) {
    return parts.join('.')
  }

  return parts.slice(-2).join('.')
}

export const getResultNumber = (resultNumber: PriorArt['resultNumber']) =>
  isNumber(resultNumber) || isUndefined(resultNumber)
    ? resultNumber
    : resultNumber.selfResultNumber

export const getParentResultNumber = (
  resultNumber: PriorArt['resultNumber']
) =>
  isNumber(resultNumber) || isUndefined(resultNumber)
    ? null
    : resultNumber.parentResultNumber
